import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getIsReseller } from 'store/publisher/selectors';
import { resetCreativeDetails } from 'store/creative/reducer';
import { Store } from 'components/common/types/Store.types';
import { CardTheme } from 'lib/Card/Card.types';
import Card from 'lib/Card';
import PreviewContentButtons from './sections/PreviewContentButtons';
import PreviewContentModals from './sections/PreviewContentModals';
import PreviewDeal from './sections/PreviewDeal';
import PreviewInfo from './sections/PreviewInfo';
import ResellerEligibleMediaOwners from './sections/ResellerEligibleMediaOwners/ResellerEligibleMediaOwners';
import TaggingRules from './sections/TaggingRules';
import { AuditLog } from './sections/AuditLog/AuditLog';
import CreativePreview from './sections/CreativePreview/CreativePreview';
import { usePreviewContentModals } from './hooks/usePreviewContentModals';
import { usePreviewContentButtons } from './hooks/usePreviewContentButtons';
import { usePreviewContentData } from './hooks/usePreviewContentData';

const PreviewContent = (): React.ReactElement => {
  const pathParams = useParams();
  const dispatch = useDispatch();
  const { marketId, commercialId, statusCode, frameSummary, deleted } = useSelector((state: Store) => state.creative);
  const isReseller = useSelector(getIsReseller);

  const {
    isFetchingCreativeDetails,
    isLoadingDealOptions,
    combinedAuditDetails,
    loadCreativesDetails,
    getFrameSummary,
    filterLoadedLookupData,
    defaultDealOptions,
    onMotionChange,
    onSelectDealId,
  } = usePreviewContentData();

  const { deleteModal, rejectModal, restoreModal, updateRejectReason, rejectReason } = usePreviewContentModals({
    loadCreativesDetails,
  });

  const {
    creativeStatus,
    isCreativeButtonDisabled,
    handleApproveCreative,
    handleFlagCreative,
    handlePendingApprovalCreative,
    handleSendForApproval,
  } = usePreviewContentButtons({ loadCreativesDetails, isFetchingCreativeDetails });

  const creativeId = pathParams.id ?? '';

  useEffect(() => {
    return () => {
      dispatch(resetCreativeDetails());
    };
  }, []);

  useEffect(() => {
    loadCreativesDetails();
  }, [creativeId]);

  return (
    <div className="grid grid-cols-12 gap-8">
      <div className="col-span-8">
        <div className="flex flex-col gap-4 mb-4">
          <PreviewInfo />

          <PreviewDeal
            filterLoadedLookupData={filterLoadedLookupData}
            defaultDealOptions={defaultDealOptions}
            isLoadingDealOptions={isLoadingDealOptions}
            onMotionChange={onMotionChange}
            onSelectDealId={onSelectDealId}
          />

          <PreviewContentModals
            deleteModal={deleteModal}
            rejectModal={rejectModal}
            restoreModal={restoreModal}
            rejectReason={rejectReason}
            updateRejectReason={updateRejectReason}
          />
        </div>

        {isReseller && <ResellerEligibleMediaOwners />}

        <TaggingRules
          marketId={marketId}
          creativeId={creativeId}
          commercialId={commercialId}
          creativeStatus={statusCode}
          isFetchingCreativeDetails={isFetchingCreativeDetails}
          frameSummary={frameSummary}
          onTaggingRulesChange={getFrameSummary}
          isDisabled={!!deleted}
        />

        <AuditLog combinedAuditDetails={combinedAuditDetails} />
      </div>

      <div className="col-span-4">
        <Card theme={CardTheme.TRANSPARENT_WITH_BORDER}>
          <div className="py-5 px-14 gap-4 flex flex-col">
            <div className="self-center">
              <CreativePreview />
            </div>
            <Card id="preview-content-buttons" theme={CardTheme.TRANSPARENT_WITH_BORDER}>
              <div className="p-4">
                <PreviewContentButtons
                  creativeStatus={creativeStatus}
                  isCreativeButtonDisabled={isCreativeButtonDisabled}
                  handleApproveCreative={handleApproveCreative}
                  handleFlagCreative={handleFlagCreative}
                  handlePendingApprovalCreative={handlePendingApprovalCreative}
                  handleSendForApproval={handleSendForApproval}
                  openDeleteModal={deleteModal.open}
                  openRejectModal={rejectModal.open}
                  openRestoreModal={restoreModal.open}
                />
              </div>
            </Card>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default PreviewContent;
